import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Separator } from '../components/ui/separator';
import { User, Mail, Moon, Sun, LogOut, Save, Loader2, Bell, BellOff, Send } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { useNavigate } from 'react-router-dom';
import { api } from '../lib/api';
import { toast } from 'sonner';

export default function Profile() {
  const { user, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const [saving, setSaving] = useState(false);
  const [savingNotifs, setSavingNotifs] = useState(false);
  const [sendingTest, setSendingTest] = useState(false);
  const [notifPrefs, setNotifPrefs] = useState({
    email_reminders: true,
    reminder_before_assessment: true,
    weekly_inactive_reminder: true,
    counselor_followup_reminder: true
  });

  useEffect(() => {
    const fetchNotifPrefs = async () => {
      try {
        const res = await api.get('/users/notification-preferences');
        setNotifPrefs(res.data);
      } catch (e) {
        console.error('Error fetching notification prefs:', e);
      }
    };
    fetchNotifPrefs();
  }, []);

  const handleLogout = async () => {
    await logout();
    navigate('/');
    toast.success('Logged out successfully');
  };

  const handleSavePreferences = async () => {
    setSaving(true);
    try {
      await api.put(`/users/preferences?theme=${theme}`);
      toast.success('Preferences saved');
    } catch (error) {
      toast.error('Failed to save preferences');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveNotifPrefs = async () => {
    setSavingNotifs(true);
    try {
      await api.put('/users/notification-preferences', notifPrefs);
      toast.success('Notification preferences saved');
    } catch (error) {
      toast.error('Failed to save notification preferences');
    } finally {
      setSavingNotifs(false);
    }
  };

  const handleSendTestEmail = async () => {
    setSendingTest(true);
    try {
      const res = await api.post('/email/test');
      if (res.data.status === 'sent') {
        toast.success('Test email sent! Check your inbox.');
      } else if (res.data.status === 'skipped') {
        toast.info('Email service not configured');
      } else {
        toast.error('Failed to send test email');
      }
    } catch (error) {
      toast.error('Failed to send test email');
    } finally {
      setSendingTest(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto space-y-6" data-testid="profile-page">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold tracking-tight">Profile Settings</h1>
        <p className="text-muted-foreground">Manage your account and preferences</p>
      </div>

      {/* User Info */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
      >
        <Card className="bg-card border-border/50">
          <CardHeader>
            <CardTitle className="text-lg font-semibold flex items-center gap-2">
              <User className="h-5 w-5 text-primary" strokeWidth={1.5} />
              Account Information
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center gap-4">
              {user?.picture ? (
                <img 
                  src={user.picture} 
                  alt={user.name} 
                  className="h-16 w-16 rounded-full border border-border"
                />
              ) : (
                <div className="h-16 w-16 rounded-full bg-primary/10 flex items-center justify-center">
                  <User className="h-8 w-8 text-primary" strokeWidth={1.5} />
                </div>
              )}
              <div>
                <h3 className="text-lg font-semibold">{user?.name}</h3>
                <p className="text-sm text-muted-foreground">{user?.email}</p>
                <span className="text-xs px-2 py-0.5 rounded-full bg-primary/10 text-primary mt-1 inline-block">
                  {user?.role}
                </span>
              </div>
            </div>

            <Separator />

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Name</Label>
                <Input value={user?.name || ''} disabled className="bg-secondary/30" />
              </div>
              <div className="space-y-2">
                <Label>Email</Label>
                <Input value={user?.email || ''} disabled className="bg-secondary/30" />
              </div>
            </div>
          </CardContent>
        </Card>
      </motion.div>

      {/* Theme Preferences */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
      >
        <Card className="bg-card border-border/50">
          <CardHeader>
            <CardTitle className="text-lg font-semibold flex items-center gap-2">
              {theme === 'dark' ? (
                <Moon className="h-5 w-5 text-primary" strokeWidth={1.5} />
              ) : (
                <Sun className="h-5 w-5 text-primary" strokeWidth={1.5} />
              )}
              Appearance
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium">Dark Mode</p>
                <p className="text-sm text-muted-foreground">
                  Switch between light and dark themes
                </p>
              </div>
              <Switch 
                checked={theme === 'dark'}
                onCheckedChange={toggleTheme}
                data-testid="theme-switch"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <button
                onClick={() => theme !== 'light' && toggleTheme()}
                className={`p-4 rounded-lg border-2 transition-all ${
                  theme === 'light' 
                    ? 'border-primary bg-primary/5' 
                    : 'border-border hover:border-border/80'
                }`}
              >
                <div className="h-24 rounded bg-white border border-gray-200 mb-2" />
                <p className="text-sm font-medium">Light</p>
              </button>
              <button
                onClick={() => theme !== 'dark' && toggleTheme()}
                className={`p-4 rounded-lg border-2 transition-all ${
                  theme === 'dark' 
                    ? 'border-primary bg-primary/5' 
                    : 'border-border hover:border-border/80'
                }`}
              >
                <div className="h-24 rounded bg-zinc-900 border border-zinc-700 mb-2" />
                <p className="text-sm font-medium">Dark</p>
              </button>
            </div>

            <Button 
              onClick={handleSavePreferences} 
              disabled={saving}
              className="w-full"
              data-testid="save-preferences"
            >
              {saving ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <>
                  <Save className="mr-2 h-4 w-4" />
                  Save Preferences
                </>
              )}
            </Button>
          </CardContent>
        </Card>
      </motion.div>

      {/* Email Notifications */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.15 }}
      >
        <Card className="bg-card border-border/50">
          <CardHeader>
            <CardTitle className="text-lg font-semibold flex items-center gap-2">
              <Bell className="h-5 w-5 text-primary" strokeWidth={1.5} />
              Email Notifications
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium text-sm">Email Reminders</p>
                <p className="text-xs text-muted-foreground">Master toggle for all email notifications</p>
              </div>
              <Switch
                checked={notifPrefs.email_reminders}
                onCheckedChange={v => setNotifPrefs(p => ({ ...p, email_reminders: v }))}
                data-testid="notif-email-reminders"
              />
            </div>

            <Separator />

            <div className="space-y-3 pl-2">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm">Assessment Reminders</p>
                  <p className="text-xs text-muted-foreground">24h before scheduled assessments</p>
                </div>
                <Switch
                  checked={notifPrefs.reminder_before_assessment}
                  onCheckedChange={v => setNotifPrefs(p => ({ ...p, reminder_before_assessment: v }))}
                  disabled={!notifPrefs.email_reminders}
                  data-testid="notif-assessment-reminder"
                />
              </div>

              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm">Weekly Check-in Reminder</p>
                  <p className="text-xs text-muted-foreground">If no assessment in 30+ days</p>
                </div>
                <Switch
                  checked={notifPrefs.weekly_inactive_reminder}
                  onCheckedChange={v => setNotifPrefs(p => ({ ...p, weekly_inactive_reminder: v }))}
                  disabled={!notifPrefs.email_reminders}
                  data-testid="notif-weekly-reminder"
                />
              </div>

              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm">Counselor Follow-up</p>
                  <p className="text-xs text-muted-foreground">Reminders for counselor sessions</p>
                </div>
                <Switch
                  checked={notifPrefs.counselor_followup_reminder}
                  onCheckedChange={v => setNotifPrefs(p => ({ ...p, counselor_followup_reminder: v }))}
                  disabled={!notifPrefs.email_reminders}
                  data-testid="notif-counselor-followup"
                />
              </div>
            </div>

            <div className="flex gap-2 pt-2">
              <Button
                onClick={handleSaveNotifPrefs}
                disabled={savingNotifs}
                className="flex-1"
                data-testid="save-notif-prefs"
              >
                {savingNotifs ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <>
                    <Save className="mr-2 h-4 w-4" />
                    Save Notifications
                  </>
                )}
              </Button>
              <Button
                variant="outline"
                onClick={handleSendTestEmail}
                disabled={sendingTest}
                data-testid="send-test-email"
              >
                {sendingTest ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <>
                    <Send className="mr-2 h-4 w-4" />
                    Test Email
                  </>
                )}
              </Button>
            </div>
          </CardContent>
        </Card>
      </motion.div>

      {/* Logout */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
      >
        <Card className="bg-card border-border/50">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-semibold text-destructive">Sign Out</h3>
                <p className="text-sm text-muted-foreground">
                  End your current session
                </p>
              </div>
              <Button 
                variant="destructive" 
                onClick={handleLogout}
                data-testid="logout-btn"
              >
                <LogOut className="mr-2 h-4 w-4" />
                Sign Out
              </Button>
            </div>
          </CardContent>
        </Card>
      </motion.div>
    </div>
  );
}
